// /integrations route — live model catalogue plus ready-to-paste client
// snippets for the three gateway protocols and three client languages.
//
// All snippet rendering is delegated to lib/integrations.ts which is
// dependency-free and unit-tested. The route is only responsible for
//   * loading the live model list from /v1/models,
//   * letting the operator pick a model,
//   * exposing protocol/language tabs, copy button, and .env.example download,
//   * keeping secrets out of the rendered code.
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Check, Copy, Download, FileText, Loader2, Plug, Search } from 'lucide-react'
import { Badge, Button, Input, Panel, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui-tabs'
import { PageHeader } from '../components/page-header'
import { fetchModels } from '../lib/gateway-client'
import { GATEWAY_ORIGIN } from '../lib/env'
import {
  DEFAULT_BASE_URL,
  DEFAULT_BASE_URL_ENV_VAR,
  DEFAULT_KEY_ENV_VAR,
  DEFAULT_SAMPLE_PROMPT,
  LANGUAGES,
  PROTOCOLS,
  renderEnvExample,
  renderSnippet,
  resolveSnippetInputs,
  type LanguageId,
  type ProtocolId,
} from '../lib/integrations'

const DEFAULT_PROTOCOL: ProtocolId = 'openai-chat'
const DEFAULT_LANGUAGE: LanguageId = 'curl'
const SNIPPET_REFETCH_MS = 30_000
const COPY_RESET_MS = 1_800

export function IntegrationsRoute() {
  const modelsQuery = useQuery({
    queryKey: ['gateway', 'models'],
    queryFn: fetchModels,
    staleTime: SNIPPET_REFETCH_MS,
    retry: 1,
  })

  const modelIds = useMemo(() => (modelsQuery.data?.data || []).map((model) => model.id), [modelsQuery.data])
  const [protocol, setProtocol] = useState<ProtocolId>(DEFAULT_PROTOCOL)
  const [language, setLanguage] = useState<LanguageId>(DEFAULT_LANGUAGE)
  const [model, setModel] = useState<string>('')
  const [search, setSearch] = useState('')
  const [copied, setCopied] = useState(false)

  // Pick a sane default model once the live list arrives. We don't override
  // the user's explicit choice, only the empty state.
  useEffect(() => {
    if (model || !modelIds.length) return
    const preferred = modelIds.find((id) => id === 'z-ai/glm-5.2') || modelIds[0]
    if (preferred) setModel(preferred)
  }, [model, modelIds])

  const filteredModels = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return modelIds
    return modelIds.filter((id) => id.toLowerCase().includes(needle))
  }, [modelIds, search])

  const gatewayBaseUrl = GATEWAY_ORIGIN || DEFAULT_BASE_URL
  const snippet = useMemo(
    () =>
      renderSnippet(
        resolveSnippetInputs({
          protocol,
          language,
          model: model || modelIds[0] || 'z-ai/glm-5.2',
          baseUrl: gatewayBaseUrl,
          keyEnvVar: DEFAULT_KEY_ENV_VAR,
          baseUrlEnvVar: DEFAULT_BASE_URL_ENV_VAR,
          prompt: DEFAULT_SAMPLE_PROMPT,
          maxTokens: 256,
        }),
      ),
    [gatewayBaseUrl, protocol, language, model, modelIds],
  )

  const envExample = useMemo(
    () =>
      renderEnvExample({
        baseUrl: gatewayBaseUrl,
        keyEnvVar: DEFAULT_KEY_ENV_VAR,
        baseUrlEnvVar: DEFAULT_BASE_URL_ENV_VAR,
      }),
    [gatewayBaseUrl],
  )

  async function copySnippet(value: string) {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else if (typeof document !== 'undefined') {
        // textarea + execCommand is the only fallback supported by jsdom +
        // older Safari; the page keeps working when clipboard is locked
        // down (e.g. http:// in production builds served from Pages).
        const node = document.createElement('textarea')
        node.value = value
        node.setAttribute('readonly', '')
        node.style.position = 'fixed'
        node.style.opacity = '0'
        document.body.appendChild(node)
        node.select()
        document.execCommand('copy')
        document.body.removeChild(node)
      }
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), COPY_RESET_MS)
    return () => window.clearTimeout(timer)
  }, [copied])

  function downloadEnv() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    const blob = new Blob([envExample], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '.env.example'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const activeProtocol = PROTOCOLS.find((entry) => entry.id === protocol) || PROTOCOLS[0]!
  const activeLanguage = LANGUAGES.find((entry) => entry.id === language) || LANGUAGES[0]!

  return (
    <div className="min-h-full bg-panel">
      <main className="mx-auto max-w-7xl space-y-4 px-4 pb-8 sm:space-y-6 sm:px-8">
        <PageHeader
          eyebrow="Integrations"
          title="Wire the gateway into your client"
          description="Copy-paste snippets for the three gateway protocols and three client languages. The model list is fetched live from /v1/models."
          actions={
            modelsQuery.data ? (
              <Badge tone="ok">
                {modelIds.length} model{modelIds.length === 1 ? '' : 's'} live
              </Badge>
            ) : null
          }
        />

        <Panel
          title={
            <span className="inline-flex items-center gap-2">
              <Plug className="h-3.5 w-3.5 text-accent-info" />Model · Protocol · Language
            </span>
          }
          actions={modelsQuery.isFetching ? <Badge tone="muted">Refreshing</Badge> : null}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Model">
              {modelsQuery.isLoading ? (
                <State icon={<Loader2 className="h-3.5 w-3.5 animate-spin" />} text="Loading live model list…" />
              ) : modelsQuery.isError || !modelIds.length ? (
                <State icon={<AlertCircle className="h-3.5 w-3.5" />} text="Model list unavailable." danger />
              ) : (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger aria-label="Model" className="w-full">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {filteredModels.map((id) => (
                      <SelectItem key={id} value={id}>
                        {id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Search">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-ink-faint" />
                <Input
                  aria-label="Filter models"
                  className="w-full pl-8"
                  placeholder="Filter models"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  disabled={!modelIds.length}
                />
              </label>
            </Field>
            <Field label="Language">
              <Select value={language} onValueChange={(value) => setLanguage(value as LanguageId)}>
                <SelectTrigger aria-label="Language" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((entry) => (
                    <SelectItem key={entry.id} value={entry.id}>
                      {entry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Tabs value={protocol} onValueChange={(value) => setProtocol(value as ProtocolId)} className="mt-5">
            <TabsList aria-label="Gateway protocol">
              {PROTOCOLS.map((entry) => (
                <TabsTrigger key={entry.id} value={entry.id}>
                  {entry.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {PROTOCOLS.map((entry) => (
              <TabsContent key={entry.id} value={entry.id}>
                <p className="text-xs leading-5 text-ink-muted">
                  Endpoint <code className="rounded bg-panel-raised px-1 py-0.5 text-[11px]">{entry.path}</code> — {entry.description}
                </p>
              </TabsContent>
            ))}
          </Tabs>
        </Panel>

        <Panel
          title={
            <span className="inline-flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-accent-info" />
              {activeProtocol.label} · {activeLanguage.label}
            </span>
          }
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => void copySnippet(snippet)} aria-label={copied ? 'Copied' : 'Copy snippet'}>
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-accent-ok" />Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />Copy
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={downloadEnv} aria-label="Download .env.example">
                <Download className="h-3.5 w-3.5" />.env.example
              </Button>
            </div>
          }
        >
          <pre
            data-testid="snippet"
            data-protocol={protocol}
            data-language={language}
            data-model={model}
            className="mono max-h-[420px] overflow-auto rounded-md border border-panel-border bg-panel-muted p-3 text-[12px] leading-5 text-ink"
          >
            {snippet}
          </pre>
          <p className="mt-3 text-xs leading-5 text-ink-muted">
            Secrets are referenced via environment variables (<code className="rounded bg-panel-raised px-1 py-0.5 text-[11px]">{DEFAULT_KEY_ENV_VAR}</code>) and never embedded in the snippet. The
            <code className="ml-1 mr-1 rounded bg-panel-raised px-1 py-0.5 text-[11px]">{DEFAULT_BASE_URL_ENV_VAR}</code>
            defaults to the public Worker origin; override <code className="rounded bg-panel-raised px-1 py-0.5 text-[11px]">GATEWAY_BASE_URL</code> when using another deployment.
          </p>
        </Panel>

        <Panel
          title={
            <span className="inline-flex items-center gap-2">
              <Download className="h-3.5 w-3.5 text-accent-info" />.env.example
            </span>
          }
          actions={
            <Button variant="outline" onClick={downloadEnv} aria-label="Download .env.example">
              <Download className="h-3.5 w-3.5" />Download
            </Button>
          }
        >
          <p className="mb-3 text-xs leading-5 text-ink-muted">
            Drop this file into your repo and replace the placeholders locally. It is safe to commit — every value is a placeholder, no real secret.
          </p>
          <pre data-testid="env-example" className="mono max-h-[260px] overflow-auto rounded-md border border-panel-border bg-panel-muted p-3 text-[12px] leading-5 text-ink">
            {envExample}
          </pre>
        </Panel>
      </main>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ink-faint">{label}</div>
      {children}
    </div>
  )
}

function State({ icon, text, danger }: { icon?: React.ReactNode; text: string; danger?: boolean }) {
  return (
    <div className={`flex items-center gap-2 rounded-md border border-panel-border bg-panel-muted px-3 py-2 text-xs ${danger ? 'text-accent-danger' : 'text-ink-muted'}`}>
      {icon}
      <span>{text}</span>
    </div>
  )
}